import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import { match } from 'ts-pattern';
import {
  TalentDropdown as Old_TalentDropdown,
  Select,
  SelectOption,
  TALENT_DROPDOWN_VALUES,
} from 'ui-components';
import { FEATURE_FLAGS, FOAM_PREFIX } from '~/constants';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { usePersistedState } from '~/hooks/usePersistedState';

export const TalentDropdown = () => {
  const savedListRosterFlag = useFeatureFlag(
    FEATURE_FLAGS.foamSavedListsRoster,
  );
  // This persistedState is used to know if the users manually updates the TalentDropdown, in that case we don't redirect
  const [, setRosterViewManuallyUpdated] = usePersistedState(
    'rosterViewManuallyUpdated',
    false,
  );

  const router = useRouter();
  const selectedView = useMemo(() => {
    const roster = router.route === '/my-roster' ? 'my-roster' : 'my-agency';

    return roster === 'my-roster'
      ? TALENT_DROPDOWN_VALUES.self
      : TALENT_DROPDOWN_VALUES.agency;
  }, [router.route]);

  const [_, setTalentOption] = usePersistedState('talentOption', selectedView);

  const handleChange = useCallback(
    (value: string) => {
      const pathname =
        value === TALENT_DROPDOWN_VALUES.agency ? '/my-agency' : '/my-roster';

      setTalentOption(value as TALENT_DROPDOWN_VALUES);
      setRosterViewManuallyUpdated(true);

      router.push({
        pathname,
        query: router.query,
      });
    },
    [router, setTalentOption, setRosterViewManuallyUpdated],
  );

  return match(savedListRosterFlag)
    .with('enabled', () => (
      <Select
        value={selectedView}
        onChange={({ value }) => handleChange(value)}
        placeholder="Talent Option"
        trackId="talent-view-roster-buttons"
        variant="borderless"
      >
        <SelectOption
          value={TALENT_DROPDOWN_VALUES.agency}
          trackId="foam-talent-view-agency"
        >
          All Talent
        </SelectOption>
        <SelectOption
          value={TALENT_DROPDOWN_VALUES.self}
          trackId="foam-talent-view-my-roster"
        >
          My Talent
        </SelectOption>
      </Select>
    ))
    .otherwise(() => (
      <Old_TalentDropdown
        defaultValue={selectedView}
        onChange={handleChange}
        prefix={FOAM_PREFIX}
        optionTrackIds={{
          [TALENT_DROPDOWN_VALUES.agency]: 'talent-view-agency',
          [TALENT_DROPDOWN_VALUES.self]: 'talent-view-my-roster',
        }}
      />
    ));
};
